import type { MouseEventHandler, ReactNode } from 'react';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { Field, Label, Switch as Toggle } from '@headlessui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';

export type SwitchChangeFn = (
  e: {
    target: {
      value: boolean;
      name: string;
    };
  },
  value: boolean,
) => void | boolean | Promise<boolean>;

export interface SwitchProps extends HelpErrorTextsProps {
  /**
   * True of false switch
   */
  checked?: boolean;
  /**
   * On checked value change
   */
  onChange?: SwitchChangeFn;
  /**
   * Switch on blur handler
   */
  onBlur?: (e: { target: { name: string } }) => void;
  /**
   * Switch on focus handler
   */
  onFocus?: (e: { target: { name: string } }) => void;
  /**
   * Switch label click handler
   */
  onLabelClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * If switch is disabled
   */
  disabled?: boolean;
  /**
   * If switch is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * If switch is required
   */
  required?: boolean;
  /**
   * Site of switch true state
   */
  side?: 'left' | 'right';
  /**
   * Site of switch true state
   */
  theme?: 'dark' | 'light';
  /**
   * Site of switch true state
   */
  size?: 'base' | 'large';
  /**
   * Label of the switch
   */
  label?: ReactNode;
  /**
   * If label should be above switch
   */
  labelUp?: boolean;
  /**
   * If label is clickable
   */
  passiveLabel?: boolean;
  /**
   * Switch name
   */
  name?: string;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Switch additional container classes
   */
  additionalClasses?: string;
  /**
   * Switch additional error classes
   */
  additionalSwitchErrorClasses?: string;
  /**
   * Switch test id
   */
  testId?: string;
}

const Switch = ({
  name = '',
  checked = false,
  onChange = EMPTY_FUNCTION,
  onBlur = EMPTY_FUNCTION,
  onFocus,
  onLabelClick,
  error = '',
  helpText = '',
  disabled = false,
  readonly = false,
  required = false,
  side = 'right',
  size = 'base',
  theme = 'dark',
  label = '',
  labelUp = false,
  passiveLabel = false,
  editors,
  additionalClasses = '',
  additionalSwitchErrorClasses = '',
  testId = '',
}: SwitchProps) => {
  const [currentChecked, setCurrentChecked] = useState(checked);

  useEffect(() => {
    setCurrentChecked(checked);
  }, [checked]);

  const handleChange = useCallback(
    async (value: boolean) => {
      setCurrentChecked(value);
      const result = await onChange({ target: { value, name } }, value);

      if (result != null) {
        setCurrentChecked(result);
      }
    },
    [onChange, name],
  );

  const handleBlur = useCallback(() => {
    onBlur({ target: { name } });
  }, [onBlur, name]);

  const handleFocus = useCallback(() => {
    onFocus?.({ target: { name } });
  }, [onFocus, name]);

  const colorClasses = {
    dark: currentChecked ? 'bg-blue' : 'bg-white dark:bg-slate-700',
    light: 'bg-slate-50',
  };

  const borderClasses = {
    dark:
      currentChecked && !disabled
        ? ''
        : 'border border-slate-200 dark:border-slate-800',
    light:
      currentChecked && !disabled
        ? 'border border-slate-200'
        : 'border dark:border-slate-800',
  };

  const markColorClasses = {
    dark: currentChecked ? 'bg-white' : 'bg-slate-400 dark:bg-slate-900',
    light: currentChecked
      ? 'bg-blue-600 dark:bg-blue'
      : 'bg-blue-600 dark:bg-blue-600 opacity-20',
  };

  const markDisabledColorClasses = {
    dark: currentChecked ? 'bg-white' : 'bg-slate-400 dark:bg-slate-800',
    light: currentChecked
      ? 'bg-slate-400 dark:bg-slate-700'
      : 'bg-slate-400 opacity-30',
  };

  const sizeClasses = {
    base: 'h-6 w-12',
    large: 'h-8 w-14',
  };

  const markTranslate = {
    left: currentChecked ? 'justify-start' : 'justify-end',
    right: currentChecked ? 'justify-end' : 'justify-start',
  };

  const markPadding = {
    base: 'px-0.5',
    large: 'px-1',
  };

  const switchLabel = useMemo(() => {
    if (!label) return null;

    const labelContent = (
      <>
        {label}
        {required && <RequiredTemplate testId={testId} />}
      </>
    );

    const positionClasses = labelUp
      ? 'text-sm text-slate-400 dark:text-slate-200 mb-1'
      : 'ml-2';

    if (onLabelClick) {
      return (
        <button
          type="button"
          disabled={disabled}
          className={twMerge(
            'dark:text-slate-200 hover:text-blue dark:hover:text-blue underline cursor-pointer',
            positionClasses,
            'disabled:cursor-not-allowed',
          )}
          onClick={onLabelClick}
          {...getTestProps(testId, 'label')}
        >
          {labelContent}
        </button>
      );
    }

    return (
      <Label
        className={twMerge(
          'dark:text-slate-200',
          positionClasses,
          !passiveLabel && 'cursor-pointer',
          disabled && !passiveLabel && 'cursor-not-allowed',
        )}
        passive={passiveLabel}
        {...getTestProps(testId, 'label')}
      >
        {labelContent}
      </Label>
    );
  }, [label, labelUp, passiveLabel, disabled, onLabelClick, testId, required]);

  return (
    <Field>
      <div
        className={twMerge(
          'w-fit items-center justify-items-center',
          labelUp ? 'grid grid-rows-2' : 'flex',
          additionalClasses,
        )}
      >
        {labelUp && switchLabel}

        <FieldEditorsIndicator
          editors={editors}
          borderRadius={9999}
          position="center"
        >
          <Toggle
            name={name}
            checked={currentChecked}
            onChange={handleChange}
            onBlur={handleBlur}
            className={twMerge(
              'block relative inline-flex items-center rounded-full',
              'focus-visible:outline-offset-2 focus-visible:outline-blue',
              borderClasses[theme] || borderClasses.dark,
              sizeClasses[size] || sizeClasses.base,
              disabled
                ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed'
                : colorClasses[theme] || colorClasses.dark,
            )}
            disabled={disabled || readonly}
            onFocus={handleFocus}
            {...getTestProps(testId, 'switch')}
          >
            <div
              className={twMerge(
                'flex h-full w-full items-center justify-end',
                markTranslate[side] || markTranslate.left,
                markPadding[size] || markPadding.base,
              )}
              {...getTestProps(testId, 'mark-container')}
            >
              <span
                className={twMerge(
                  'pointer-events-none relative h-[80%] aspect-square inline-block rounded-full',
                  'bg-white dark:bg-slate-900 shadow-sm transform ring-0 transition ease-in-out duration-200',
                  disabled
                    ? markDisabledColorClasses[theme] ||
                        markDisabledColorClasses.dark
                    : markColorClasses[theme] || markColorClasses.dark,
                )}
                {...getTestProps(testId, 'mark')}
              ></span>
            </div>
          </Toggle>
        </FieldEditorsIndicator>

        {!labelUp && switchLabel}
      </div>
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={error}
        additionalErrorClasses={additionalSwitchErrorClasses}
        testId={testId}
      />
    </Field>
  );
};

export default Switch;
